import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Translate as TranslateIcon,
  PhoneAndroid as MobileIcon,
  Language as WebIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

interface Language {
  _id: string;
  name: string;
  code: string;
  nativeName: string;
  isActive: boolean;
}

const GROUPS = [
  { value: 'all', label: 'All' },
  { value: 'labels', label: 'Labels' },
  { value: 'descriptions', label: 'Descriptions' },
  { value: 'headings', label: 'Headings' },
  { value: 'buttons', label: 'Buttons' },
  { value: 'success_messages', label: 'Success Messages' },
  { value: 'error_messages', label: 'Error Messages' },
  { value: 'placeholders', label: 'Placeholders' },
  { value: 'titles', label: 'Titles' },
  { value: 'message_codes', label: 'Message Codes' }  
];

type StringType = 'APP_STRINGS' | 'WEB_STRINGS';

const StringTranslationManagement: React.FC = () => {
  const [stringType, setStringType] = useState<StringType>('APP_STRINGS');
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [strings, setStrings] = useState<Record<string, string>>({});
  const [allStrings, setAllStrings] = useState<Record<string, { group: string; value: string }>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [selectedKeyGroup, setSelectedKeyGroup] = useState<string>('');
  const [formData, setFormData] = useState({
    key: ''
  });
  const [languageValues, setLanguageValues] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isTranslating, setIsTranslating] = useState(false);

  const queryClient = useQueryClient();

  // Fetch languages
  const { data: languages, isLoading: languagesLoading } = useQuery({
    queryKey: ['languages'],
    queryFn: async () => {
      const response = await apiService.getLanguages();
      return response.data as Language[];
    }
  });

  // Fetch strings for default language (for display purposes)
  const defaultLanguage = languages?.find(l => l.isActive) || languages?.[0];
  const { data: stringsData, isLoading: stringsLoading } = useQuery({
    queryKey: ['strings', defaultLanguage?.code, stringType],
    queryFn: async () => {
      if (!defaultLanguage) return null;
      const response = await apiService.getStringsByLanguage(defaultLanguage.code, stringType);
      return response.data;
    },
    enabled: !!defaultLanguage
  });

  // Update strings when data changes
  useEffect(() => {
    if (stringsData) {
      if (selectedGroup === 'all') {
        // Combine all groups into a single object with group info
        const combined: Record<string, { group: string; value: string }> = {};
        Object.keys(stringsData).forEach((group) => {
          if (stringsData[group] && typeof stringsData[group] === 'object') {
            Object.keys(stringsData[group]).forEach((key) => {
              combined[`${group}.${key}`] = {
                group: group,
                value: stringsData[group][key]
              };
            });
          }
        });
        setAllStrings(combined);
        setStrings({});
      } else {
        setStrings(stringsData[selectedGroup] || {});
        setAllStrings({});
      }
    } else {
      setStrings({});
      setAllStrings({});
    }
  }, [stringsData, selectedGroup]);




  // Upsert string mutation
  const upsertMutation = useMutation({
    mutationFn: async (data: { languageCode: string; group: string; key: string; value: string; description?: string; type?: StringType }) => {
      const response = await apiService.upsertString({ ...data, type: data.type || stringType });
      return response.data;
    },
    onSuccess: () => {
      if (defaultLanguage) {
        queryClient.invalidateQueries({ queryKey: ['strings', defaultLanguage.code, stringType] });
      }
      setSnackbar({ open: true, message: 'String saved successfully', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to save string', severity: 'error' });
    }
  });

  // Update entire language data mutation
  const updateLanguageDataMutation = useMutation({
    mutationFn: async ({ languageCode, data }: { languageCode: string; data: any }) => {
      const response = await apiService.updateLanguageData(languageCode, data, stringType);
      return response.data;
    },
    onSuccess: () => {
      if (defaultLanguage) {
        queryClient.invalidateQueries({ queryKey: ['strings', defaultLanguage.code, stringType] });
      }
      setSnackbar({ open: true, message: 'Language data updated successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to update language data', severity: 'error' });
    }
  });


  // Delete string mutation
  const deleteMutation = useMutation({
    mutationFn: async ({ languageCode, group, key }: { languageCode: string; group: string; key: string }) => {
      const response = await apiService.deleteString(languageCode, group, key, stringType);
      return response.data;
    },
    onSuccess: () => {
      if (defaultLanguage) {
        queryClient.invalidateQueries({ queryKey: ['strings', defaultLanguage.code, stringType] });
      }
      // Also invalidate for all other languages
      if (languages) {
        languages.filter(l => l.isActive).forEach(lang => {
          queryClient.invalidateQueries({ queryKey: ['strings', lang.code, stringType] });
        });
      }
      setSnackbar({ open: true, message: 'String deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      setSelectedKey('');
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to delete string', severity: 'error' });
    }
  });

  const handleOpenDialog = async (key?: string, group?: string) => {
    if (key) {
      const actualKey = selectedGroup === 'all' ? key.split('.').slice(1).join('.') : key;
      const actualGroup = selectedGroup === 'all' ? (group || key.split('.')[0]) : selectedGroup;
      
      setSelectedKey(actualKey);
      setSelectedKeyGroup(actualGroup);
      setFormData({
        key: actualKey
      });
      
      // Fetch translations for this key across all languages
      if (languages) {
        const translations: Record<string, string> = {};
        const fetchPromises = languages
          .filter(l => l.isActive)
          .map(async (lang) => {
            try {
              const response = await apiService.getStringsByLanguageAndGroup(lang.code, actualGroup, stringType);
              if (response.data && response.data[actualKey]) {
                translations[lang.code] = response.data[actualKey];
              } else {
                translations[lang.code] = '';
              }
            } catch (error) {
              translations[lang.code] = '';
            }
          });
        await Promise.all(fetchPromises);
        setLanguageValues(translations);
      }
    } else {
      setSelectedKey('');
      setSelectedKeyGroup(selectedGroup === 'all' ? 'labels' : selectedGroup);
      setFormData({
        key: ''
      });
      // Initialize with empty values for all languages
      if (languages) {
        const emptyValues: Record<string, string> = {};
        languages.filter(l => l.isActive).forEach(lang => {
          emptyValues[lang.code] = '';
        });
        setLanguageValues(emptyValues);
      }
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedKey('');
    setSelectedKeyGroup(selectedGroup === 'all' ? 'labels' : selectedGroup);
    setFormData({
      key: ''
    });
    setLanguageValues({});
  };

  const handleSubmit = async () => {
    if (!formData.key) {
      setSnackbar({ open: true, message: 'Please fill in the key field', severity: 'error' });
      return;
    }

    const groupToUse = selectedGroup === 'all' ? selectedKeyGroup : selectedGroup;
    
    // Check if at least one language has a value
    const hasValue = Object.values(languageValues).some(val => val && val.trim());
    if (!hasValue) {
      setSnackbar({ open: true, message: 'Please provide at least one translation', severity: 'error' });
      return;
    }

    // Save translations for all languages
    if (languages) {
      const savePromises = languages
        .filter(l => l.isActive)
        .map(async (lang) => {
          const value = languageValues[lang.code]?.trim() || '';
          if (value) {
            try {
              await apiService.upsertString({
                languageCode: lang.code,
                group: groupToUse,
                key: formData.key,
                value: value,
                description: '',
                type: stringType
              });
            } catch (error) {
              console.error(`Failed to save translation for ${lang.code}:`, error);
            }
          }
        });

      await Promise.all(savePromises);

      // Invalidate queries for all languages
      languages.filter(l => l.isActive).forEach(lang => {
        queryClient.invalidateQueries({ queryKey: ['strings', lang.code, stringType] });
      });

      setSnackbar({ open: true, message: 'Translations saved successfully', severity: 'success' });
      handleCloseDialog();
    }
  };

  const handleDelete = () => {
    if (selectedKey && languages) {
      const groupToUse = selectedGroup === 'all' ? selectedKeyGroup : selectedGroup;
      // Delete for all languages
      const deletePromises = languages
        .filter(l => l.isActive)
        .map(lang =>
          apiService.deleteString(lang.code, groupToUse, selectedKey, stringType)
        );

      Promise.all(deletePromises).then(() => {
        if (defaultLanguage) {
          queryClient.invalidateQueries({ queryKey: ['strings', defaultLanguage.code, stringType] });
        }
        languages.filter(l => l.isActive).forEach(lang => {
          queryClient.invalidateQueries({ queryKey: ['strings', lang.code, stringType] });
        });
        setSnackbar({ open: true, message: 'String deleted successfully for all languages', severity: 'success' });
        setDeleteDialogOpen(false);
        setSelectedKey('');
      }).catch(() => {
        setSnackbar({ open: true, message: 'Error deleting string', severity: 'error' });
      });
    }
  };

  const handleTranslate = async () => {
    // Find English language from languages array
    const englishLang = languages?.find(l => 
      l.code.toUpperCase() === 'EN' || 
      l.code.toLowerCase() === 'en' ||
      l.name.toLowerCase() === 'english'
    );
    
    const englishLangCode = englishLang?.code || 'en';
    const englishLangCodeUpper = englishLangCode.toUpperCase();
    const englishLangCodeLower = englishLangCode.toLowerCase();
    
    // Get English translation value
    const englishValue = languageValues[englishLangCode] ?? 
                        languageValues[englishLangCodeUpper] ?? 
                        languageValues[englishLangCodeLower] ?? 
                        languageValues['EN'] ?? 
                        languageValues['en'] ?? '';

    if (!englishValue || !englishValue.trim()) {
      toast.error('Please provide English translation to translate from');
      return;
    }

    setIsTranslating(true);

    try {
      const activeLanguages = languages?.filter(l => {
        const code = l.code.toUpperCase();
        return l.isActive && code !== 'EN' && code !== englishLangCodeUpper;
      }) || [];
      
      if (activeLanguages.length === 0) {
        toast.error('No active languages found to translate to');
        setIsTranslating(false);
        return;
      }
      
      // Initialize final values with current state
      let finalLanguageValues = { ...languageValues };
      let translatedCount = 0;
      
      // Translate to all active languages except English
      for (const lang of activeLanguages) {
        const langCode = lang.code.toUpperCase();
        const langCodeLower = lang.code.toLowerCase();
        
        const currentValue = finalLanguageValues[langCode] || finalLanguageValues[langCodeLower] || '';
        
        // Only translate if the field is empty
        if (!currentValue || currentValue.trim() === '') {
          try {
            const response = await apiService.translateText(englishValue, langCode, 'en');
            
            if (response.success && response.data?.translatedText) {
              finalLanguageValues[langCode] = response.data.translatedText;
              translatedCount++;
            } else {
              toast.error(`Failed to translate to ${lang.name}`);
            }
          } catch (error: any) {
            console.error(`Error translating to ${langCode}:`, error);
            toast.error(`Failed to translate to ${lang.name}: ${error.message || 'Unknown error'}`);
          }
        }
      }

      setLanguageValues(finalLanguageValues);
      
      if (translatedCount > 0) {
        toast.success(`Translated to ${translatedCount} language(s) successfully`);
      } else {
        toast.error('No translations were made. All languages may already have content.');
      }
    } catch (error: any) {
      console.error('Translation error:', error);
      toast.error(error.response?.data?.message || 'Failed to translate content. Please check if translation API is configured.');
    } finally {
      setIsTranslating(false);
    }
  };

  // Filter strings based on search query
  const getFilteredStrings = () => {
    if (selectedGroup === 'all') {
      const filtered: Record<string, { group: string; value: string }> = {};
      Object.keys(allStrings).forEach((key) => {
        const searchLower = searchQuery.toLowerCase();
        const keyLower = key.toLowerCase();
        const valueLower = allStrings[key].value.toLowerCase();
        const groupLower = allStrings[key].group.toLowerCase();
        
        if (keyLower.includes(searchLower) || valueLower.includes(searchLower) || groupLower.includes(searchLower)) {
          filtered[key] = allStrings[key];
        }
      });
      return filtered;
    } else {
      const filtered: Record<string, string> = {};
      Object.keys(strings).forEach((key) => {
        const searchLower = searchQuery.toLowerCase();
        const keyLower = key.toLowerCase();
        const valueLower = strings[key].toLowerCase();
        
        if (keyLower.includes(searchLower) || valueLower.includes(searchLower)) {
          filtered[key] = strings[key];
        }
      });
      return filtered;
    }
  };

  if (languagesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">String Translation Management</Typography>
      </Box>

      {/* String Type Selector - APP vs WEB */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="subtitle1" fontWeight="medium">
              String Type:
            </Typography>
            <ToggleButtonGroup
              value={stringType}
              exclusive
              onChange={(_, newValue) => {
                if (newValue !== null) {
                  setStringType(newValue);
                }
              }}
              size="small"
            >
              <ToggleButton value="APP_STRINGS" sx={{ px: 3 }}>
                <MobileIcon sx={{ mr: 1 }} />
                Mobile App
              </ToggleButton>
              <ToggleButton value="WEB_STRINGS" sx={{ px: 3 }}>
                <WebIcon sx={{ mr: 1 }} />
                Web App
              </ToggleButton>
            </ToggleButtonGroup>
            <Chip
              label={stringType === 'APP_STRINGS' ? 'Managing mobile app strings' : 'Managing web app strings'}
              color={stringType === 'APP_STRINGS' ? 'primary' : 'secondary'}
              variant="outlined"
              size="small"
            />
          </Box>
        </CardContent>
      </Card>

      <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Tabs
                value={selectedGroup}
                onChange={(_, newValue) => setSelectedGroup(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {GROUPS.map((group) => (
                  <Tab key={group.value} label={group.label} value={group.value} />
                ))}
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                <Typography variant="h6">
                  {GROUPS.find(g => g.value === selectedGroup)?.label} ({
                    selectedGroup === 'all' 
                      ? Object.keys(getFilteredStrings()).length 
                      : Object.keys(getFilteredStrings()).length
                  } strings)
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <TextField
                    placeholder="Search strings..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    sx={{ minWidth: 250 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    disabled={stringsLoading || selectedGroup === 'all'}
                  >
                    Add String
                  </Button>
                </Box>
              </Box>

              {stringsLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        {selectedGroup === 'all' && <TableCell>Group</TableCell>}
                        <TableCell>Key</TableCell>
                        <TableCell>Value</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const filtered = getFilteredStrings();
                        const filteredKeys = Object.keys(filtered);
                        
                        return filteredKeys.length > 0 ? (
                          filteredKeys.map((key) => {
                            if (selectedGroup === 'all') {
                              const { group, value } = allStrings[key];
                              const actualKey = key.split('.').slice(1).join('.');
                              return (
                                <TableRow key={key}>
                                  <TableCell>
                                    <Chip label={group} size="small" color="primary" variant="outlined" />
                                  </TableCell>
                                  <TableCell>
                                    <Chip label={actualKey} size="small" />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                                      {value}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDialog(key, group)}
                                      color="primary"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setSelectedKey(actualKey);
                                        setSelectedKeyGroup(group);
                                        setDeleteDialogOpen(true);
                                      }}
                                      color="error"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            } else {
                              const value = strings[key];
                              return (
                                <TableRow key={key}>
                                  <TableCell>
                                    <Chip label={key} size="small" />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                                      {value}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDialog(key)}
                                      color="primary"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setSelectedKey(key);
                                        setDeleteDialogOpen(true);
                                      }}
                                      color="error"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            }
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={selectedGroup === 'all' ? 4 : 3} align="center">
                              <Typography color="textSecondary">
                                {searchQuery ? 'No strings found matching your search' : 'No strings found for this group'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
      </>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{selectedKey ? 'Edit String' : 'Add String'}</Typography>
            {languages && languages.filter(l => l.isActive).length > 1 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<TranslateIcon />}
                onClick={handleTranslate}
                disabled={isTranslating || (!languageValues['EN'] && !languageValues['en'])}
              >
                {isTranslating ? 'Translating...' : 'Translate All'}
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {(selectedGroup === 'all' || !selectedKey) && (
              <FormControl fullWidth>
                <InputLabel>Group</InputLabel>
                <Select
                  value={selectedKeyGroup || (selectedGroup === 'all' ? 'labels' : selectedGroup)}
                  onChange={(e) => setSelectedKeyGroup(e.target.value)}
                  label="Group"
                  disabled={!!selectedKey}
                >
                  {GROUPS.filter(g => g.value !== 'all').map((group) => (
                    <MenuItem key={group.value} value={group.value}>
                      {group.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Key"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              required
              fullWidth
              disabled={!!selectedKey}
              helperText="Unique identifier for this string (e.g., 'app_name', 'welcome_message')"
            />
            {languages && languages.filter(l => l.isActive).map((lang) => {
              const isEnglish = lang.code.toUpperCase() === 'EN' || lang.code.toLowerCase() === 'en';
              return (
                <TextField
                  key={lang.code}
                  label={`Translation (${lang.name} - ${lang.nativeName})${isEnglish ? ' *' : ''}`}
                  value={languageValues[lang.code] || ''}
                  onChange={(e) => {
                    setLanguageValues({
                      ...languageValues,
                      [lang.code]: e.target.value
                    });
                  }}
                  fullWidth
                  multiline
                  rows={3}
                  helperText={isEnglish ? 'Required - This will be used as source for translation' : `Translation for ${lang.nativeName}`}
                  required={isEnglish}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete String</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the string "{selectedKey}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleteMutation.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StringTranslationManagement;

